import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  whoIsArriving,
  whoIsLeaving,
  listInternalTransfersOnDate,
} from "../api/transfersApi.js";

export default function useTransfersByDate(selectedDate) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dateStr = useMemo(
    () => (selectedDate ? selectedDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")),
    [selectedDate]
  );

  const refetch = useCallback(
    async (date = selectedDate) => {
      const formattedDate = (date || dayjs()).format("YYYY-MM-DD");
      setLoading(true);
      setError(null);

      try {
        const [arrData, depData, internalData] = await Promise.all([
          whoIsArriving(formattedDate),
          whoIsLeaving(formattedDate),
          listInternalTransfersOnDate(formattedDate),
        ]);

        const internal = (internalData || []).map((t) => ({
          ...t,
          direction: "INTERNAL",
          campus: t.fromCampus || "Unknown",
          // important: make INTERNAL look "planned" to the Transfers table
          transferId: t.transferId || t.id || 1,
        }));

        // Build coverage set for BOTH students and adults
        // Key: "<TYPE>-<ID>-<DATE>"
        const coveredByInternal = new Set(
          internal.map((t) => {
            const type = t.studentId ? "STUDENT" : t.adultId ? "ADULT" : "UNKNOWN";
            const id = t.studentId || t.adultId || 0;
            return `${type}-${id}-${t.transferDate}`;
          })
        );

        // Helper: does this IN/OUT row represent the same person/date?
        const isCovered = (row) => {
          const type = row.studentId ? "STUDENT" : row.adultId ? "ADULT" : "UNKNOWN";
          const id = row.studentId || row.adultId || 0;
          return coveredByInternal.has(`${type}-${id}-${formattedDate}`);
        };

        // Normal IN/OUT rows
        const inout = [
          ...(arrData || []).map((t) => ({ ...t, direction: "IN" })),
          ...(depData || []).map((t) => ({ ...t, direction: "OUT" })),
        ];

        // If internal transfer exists, remove the missing placeholder rows for that person/date.
        // We only suppress rows that are "missing" (no transferId), so real external transfers remain.
        const inoutFiltered = inout.filter((t) => {
          const missing = !t.transferId || t.transferId === 0;
          return !(missing && isCovered(t));
        });

        const combined = [...inoutFiltered, ...internal];
        setTransfers(combined);
        return combined;
      } catch (e) {
        console.error("[useTransfersByDate] fetch failed:", e);
        setError(e);
        setTransfers([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [selectedDate]
  );

  // Keep same behaviour as container: refetch on date change
  useEffect(() => {
    refetch(selectedDate);
  }, [refetch, selectedDate]);

  return { transfers, loading, error, refetch, dateStr };
}
